import { formatWithCommas } from './libs';

// Value axes that step in round numbers (0, 250K, 500K, 750K, 1M) instead of
// fifths of the largest bar (0, 164.3K, 328.5K …).

export type NiceAxis = {
  maxValue: number;
  noOfSections: number;
  stepValue: number;
};

const NICE_FRACTIONS = [1, 2, 2.5, 5, 10];

/** Smallest round step that fits `maxData` in about `targetSections` steps. */
export function getNiceAxis(maxData: number, targetSections = 4): NiceAxis {
  const sections = Math.max(1, Math.round(targetSections));
  const max = Number.isFinite(maxData) && maxData > 0 ? maxData : 0;
  if (max === 0) {
    return { maxValue: 1000, noOfSections: 4, stepValue: 250 };
  }
  const rawStep = max / sections;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const fraction =
    NICE_FRACTIONS.find(f => f * magnitude >= rawStep) ?? NICE_FRACTIONS[4];
  const stepValue = fraction * magnitude;
  // The epsilon keeps float noise (4.0000000001) from adding an empty step.
  const noOfSections = Math.max(1, Math.ceil(max / stepValue - 1e-9));
  return { maxValue: stepValue * noOfSections, noOfSections, stepValue };
}

const UNITS: Array<[number, string]> = [
  [1e9, 'B'],
  [1e6, 'M'],
  [1e3, 'K'],
];

/**
 * Tick label for a {@link getNiceAxis} axis: "750K", "1M", "2.5K". Steps under
 * 1,000 stay plain numbers ("500", "1,000") so an axis never mixes "800" with
 * "1.2K".
 */
export function formatNiceAxisTick(value: number, stepValue: number): string {
  const n = Number(value) || 0;
  if (n === 0) return '0';
  if (stepValue < 1000) return formatWithCommas(n, 0);
  const unit = UNITS.find(([divisor]) => Math.abs(n) >= divisor);
  return unit
    ? `${formatWithCommas(n / unit[0], 2)}${unit[1]}`
    : formatWithCommas(n, 0);
}
